import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, KeyRound, Hammer, Building2, Grid3x3,
  LandPlot, BookOpen, Tractor, Factory, Star, ChevronDown,
  Upload, Eye, CheckCircle2,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import ToolkitCard from "@/components/ToolkitCard";
import Seo from "@/components/Seo";
import { FadeIn, MaskedLine, Eyebrow } from "@/components/Reveal";
import { IMAGES, PATHWAYS, METHODOLOGY, AUDIENCES, FAQS, TESTIMONIALS } from "@/data/site";
import { api } from "@/lib/api";

const ICONS = { KeyRound, Hammer, Building2, Grid3x3, LandPlot, BookOpen, Tractor, Factory };

const Contours = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
    {[14, 26, 38, 50, 62, 74, 86].map((r) => (
      <path
        key={r}
        d={`M0 ${r} C 25 ${r - 8}, 45 ${r + 7}, 70 ${r - 4} S 95 ${r + 3}, 100 ${r}`}
        fill="none"
        stroke="#D8CCBB"
        strokeWidth="0.4"
      />
    ))}
  </svg>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative bg-ivory overflow-hidden">
      <Contours className="absolute -right-24 -top-24 w-[640px] h-[640px] opacity-50 animate-drift-slow" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <Eyebrow>Making NSW Planning Simple</Eyebrow>
          </motion.div>
          <h1
            className="mt-6 font-serif font-normal tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl text-teal"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.25}>Before You Buy.</MaskedLine>
            <MaskedLine delay={0.4}>Before You Build.</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="italic underline decoration-sand decoration-[3px] underline-offset-8">
                Plan First.
              </span>
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-7 max-w-xl text-base sm:text-lg text-charcoal/75 leading-relaxed font-light"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
          >
            Property planning in NSW doesn’t need to feel overwhelming. Explore practical guides,
            interactive toolkits and professional planning advice designed to help you understand
            your property, navigate the development process and move forward with confidence.
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <Link
              to="/toolkits"
              data-testid="hero-cta-explore-toolkits"
              className="inline-flex items-center gap-2 bg-teal text-ivory font-semibold px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors duration-300"
            >
              Explore Planning Toolkits <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/hub"
              data-testid="hero-cta-explore-hub"
              className="inline-flex items-center gap-2 border border-teal/40 text-teal px-7 py-3.5 rounded-full hover:border-teal hover:bg-teal hover:text-ivory transition-colors duration-300"
            >
              Start Learning
            </Link>
            <Link
              to="/book"
              data-testid="hero-cta-book-consult"
              className="inline-flex items-center gap-2 text-teal px-2 py-3.5 underline underline-offset-8 decoration-sand hover:decoration-teal transition-colors duration-300"
            >
              Planner Connect — coming soon <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <motion.div
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
          >
            <span>Residential Planning</span>
            <span>Property Development</span>
            <span>Due Diligence</span>
          </motion.div>
        </div>
        <div className="lg:col-span-6 relative">
          <motion.div
            style={{ y }}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="border border-cloud bg-white p-3 shadow-xl">
              <img
                src={IMAGES.interior_plans}
                alt="Sunlit coastal NSW home interior with renovation plans and material samples"
                className="w-full aspect-[16/11] object-cover"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-teal text-ivory px-6 py-5 shadow-xl max-w-[250px]">
              <p className="font-serif text-lg leading-snug">
                Knowledge should come before commitment.
              </p>
              <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-sand">
                Our founding belief
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const BrandBanner = () => (
  <motion.section
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    className="border-y border-cloud"
    data-testid="brand-banner"
  >
    <img
      src="/assets/brand-banner.jpg"
      alt="PLAN FIRST — Understand before you commit. Residential planning, property development, due diligence."
      className="w-full h-auto block"
    />
  </motion.section>
);

const DaFlagship = () => (
  <section className="bg-teal py-20 lg:py-28" data-testid="da-flagship">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <FadeIn className="lg:col-span-7">
        <Eyebrow dark>Plan First · Post-Approval Roadmap</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ivory leading-[1.08]">
          Your DA is approved. What now?
        </h2>
        <p className="mt-5 text-ivory/80 leading-relaxed max-w-xl">
          You've received your development consent — but now you're faced with pages of
          conditions, certificates, contributions and requirements. Upload your consent and
          receive a personalised, easy-to-follow roadmap explaining what your conditions mean,
          what needs to happen next, and where to find the relevant council resources.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/da-approved/upload"
            data-testid="da-flagship-upload"
            className="inline-flex items-center gap-2 bg-ivory text-teal text-sm font-medium px-7 py-3.5 rounded-full hover:bg-sand transition-colors"
          >
            <Upload className="w-4 h-4" /> Upload your development consent
          </Link>
          <Link
            to="/da-approved"
            data-testid="da-flagship-sample"
            className="inline-flex items-center gap-2 border border-ivory/40 text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-ivory/10 transition-colors"
          >
            <Eye className="w-4 h-4" /> See a sample roadmap
          </Link>
        </div>
        <p className="mt-5 text-xs font-mono uppercase tracking-[0.16em] text-sand/90">
          Personalised service · A$69 · GST included
        </p>
      </FadeIn>
      <FadeIn delay={0.12} className="lg:col-span-5">
        <ul className="space-y-4">
          {[
            "Every condition explained in plain English, linked to your actual consent",
            "Staged from Construction Certificate to Occupation Certificate",
            "How to lodge each follow-on approval — and where to find it",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-ivory/85 leading-relaxed bg-ivory/5 border border-ivory/10 px-5 py-4">
              <CheckCircle2 className="w-5 h-5 text-sand shrink-0 mt-0.5" />
              {t}
            </li>
          ))}
        </ul>
      </FadeIn>
    </div>
  </section>
);

const FOUNDING_COLLECTION = [
  {
    title: "Your DA Is Approved. What Now?",
    blurb: "An individualised roadmap generated from your uploaded development consent conditions.",
    badge: "Flagship service",
    to: "/da-approved",
    testid: "founding-da",
  },
  {
    title: "Before You Buy",
    blurb: "Property due diligence — understand the risks before you purchase.",
    badge: "Toolkits on sale",
    to: "/start/buying",
    testid: "founding-buying",
  },
  {
    title: "Renovating Your Home",
    blurb: "Approvals, design considerations and future-proofing for NSW homeowners.",
    badge: "Guide on sale",
    to: "/start/renovating",
    testid: "founding-renovating",
  },
  {
    title: "Know Your Land",
    blurb: "Land zoning, planning constraints and property opportunities.",
    badge: "Guide on sale",
    to: "/toolkits",
    testid: "founding-land",
  },
];

const FoundingCollection = () => (
  <section className="py-20 lg:py-28 bg-sand-light/60" data-testid="founding-collection">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Eyebrow>The Founding Collection</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
          Understand your property. Know your next step.
        </h2>
      </FadeIn>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cloud border border-cloud">
        {FOUNDING_COLLECTION.map((c, i) => (
          <FadeIn key={c.testid} delay={i * 0.06} className="h-full">
            <Link
              to={c.to}
              data-testid={c.testid}
              className="group flex flex-col h-full bg-white p-7 hover:bg-teal transition-colors duration-500"
            >
              <span className="self-start text-[10px] font-mono uppercase tracking-[0.14em] bg-sand-light text-charcoal/60 px-2.5 py-1 group-hover:bg-ivory/10 group-hover:text-sand transition-colors duration-500">
                {c.badge}
              </span>
              <h3 className="mt-5 font-serif text-xl text-teal group-hover:text-ivory transition-colors duration-500">
                {c.title}
              </h3>
              <p className="mt-2 text-sm text-charcoal/70 group-hover:text-ivory/70 leading-relaxed transition-colors duration-500">
                {c.blurb}
              </p>
              <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-teal/70 group-hover:text-sand transition-colors duration-500">
                Explore <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Pathways = () => (
  <section className="py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Eyebrow>Start Where You Are</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
          What are you planning?
        </h2>
        <p className="mt-4 max-w-2xl text-charcoal/70 leading-relaxed">
          Choose the path closest to your project. Each one leads to plain-English guidance,
          practical checklists and the right next step.
        </p>
      </FadeIn>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cloud border border-cloud">
        {PATHWAYS.map((p, i) => {
          const Icon = ICONS[p.icon];
          return (
            <FadeIn key={p.id} delay={i * 0.06} className="h-full">
              <Link
                to={p.to}
                data-testid={`pathway-card-${p.id}`}
                className="group flex flex-col h-full bg-white p-8 hover:bg-teal transition-colors duration-500"
              >
                <Icon className="w-7 h-7 text-teal group-hover:text-sand transition-colors duration-500" />
                <h3 className="mt-5 font-serif text-xl text-teal group-hover:text-ivory transition-colors duration-500">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-charcoal/70 group-hover:text-ivory/70 leading-relaxed transition-colors duration-500">
                  {p.blurb}
                </p>
                <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-teal/70 group-hover:text-sand transition-colors duration-500">
                  Explore <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

const Methodology = () => (
  <section className="bg-white border-y border-cloud py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Eyebrow>Our Method</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
          Educate first. Commit second.
        </h2>
      </FadeIn>
      <div className="mt-12 grid md:grid-cols-3 lg:grid-cols-5 gap-px bg-cloud border border-cloud">
        {METHODOLOGY.map((m, i) => (
          <FadeIn key={m.step} delay={i * 0.08} className="h-full">
            <div
              className="h-full bg-white p-7 hover:bg-sand-light transition-colors duration-500"
              data-testid={`methodology-step-${m.step}`}
            >
              <span className="font-mono text-teal/40 text-sm">{m.step}</span>
              <h3 className="mt-4 font-serif text-lg tracking-[0.06em] text-teal">{m.title}</h3>
              <p className="mt-3 text-sm text-charcoal/65 leading-relaxed">{m.description}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Audiences = () => (
  <section className="py-20 lg:py-28 bg-sand-light">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Eyebrow>Who We Help</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
          From first homes to full developments
        </h2>
      </FadeIn>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {AUDIENCES.map((a, i) => (
          <FadeIn key={a.id} delay={i * 0.08} className="h-full">
            <Link
              to={a.to}
              data-testid={`audience-card-${a.id}`}
              className="group block h-full bg-white border border-cloud hover:border-teal transition-all duration-300 hover:-translate-y-1 hover:shadow-lg overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES[a.image]}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-lg text-teal">{a.title}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{a.hook}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal group-hover:gap-2.5 transition-all">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedGuide = () => (
  <section className="py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <FadeIn>
        <div className="border border-cloud bg-white p-3 shadow-lg">
          <img
            src={IMAGES.renovation_before_after}
            alt="Before and after: a classic brick home transformed into a bright coastal renovation"
            loading="lazy"
            className="w-full aspect-[16/10] object-cover object-[50%_60%]"
            data-testid="featured-guide-image"
          />
        </div>
      </FadeIn>
      <FadeIn delay={0.12}>
        <Eyebrow>Featured Guide</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal text-balance">
          Before you renovate, know what your block allows
        </h2>
        <p className="mt-5 text-charcoal/70 leading-relaxed max-w-lg">
          The difference between these two homes isn't just design — it's planning. Our
          renovation toolkit walks you through the rules, approval pathways and neighbourhood
          considerations before you spend a dollar on drawings.
        </p>
        <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/50">
          NSW Homeowner Guide · Instant PDF
        </p>
        <Link
          to="/toolkits"
          data-testid="featured-guide-cta"
          className="mt-8 inline-flex items-center gap-2 bg-teal text-ivory font-semibold px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
        >
          Get the renovation toolkit <ArrowRight className="w-4 h-4" />
        </Link>
      </FadeIn>
    </div>
  </section>
);

const FeaturedToolkits = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.available).slice(0, 3))).catch(() => {});
  }, []);
  if (!products.length) return null;
  return (
    <section className="py-20 lg:py-28 bg-sand-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>The Digital Store</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
              Toolkits that pay for themselves
            </h2>
          </div>
          <Link
            to="/toolkits"
            className="inline-flex items-center gap-2 text-teal font-medium border-b-2 border-sand pb-1 hover:border-teal transition-colors"
          >
            Browse all toolkits <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <ToolkitCard
              key={p.id}
              product={p}
              index={i}
              added={false}
              onAdd={() => (window.location.href = "/toolkits")}
              onDetails={() => (window.location.href = "/toolkits")}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const AdvisorSpotlight = () => (
  <section className="py-20 lg:py-28 overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <FadeIn className="lg:col-span-5">
        <div className="border border-cloud bg-white p-10 lg:p-14 shadow-lg flex items-center justify-center">
          <img
            src="/assets/logo_circle.jpeg"
            alt="PLAN FIRST — Understand before you commit."
            loading="lazy"
            className="w-full max-w-[320px] h-auto"
            data-testid="advisor-spotlight-image"
          />
        </div>
      </FadeIn>
      <div className="lg:col-span-7">
        <FadeIn>
          <Eyebrow>Why We Exist</Eyebrow>
          <blockquote className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug tracking-tight text-teal text-balance">
            “We’ve sat on the council side of the counter. We know exactly where applications
            stall — and how to make sure yours doesn’t.”
          </blockquote>
          <p className="mt-6 text-charcoal/70 leading-relaxed max-w-xl">
            PLAN FIRST NSW was founded by a NSW planning professional with more than 20 years
            across customer service, property development, planning and government — turning that
            experience into guidance any property owner can use.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/book"
              data-testid="spotlight-cta-book"
              className="inline-flex items-center gap-2 bg-teal text-ivory font-semibold px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
            >
              Book a consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 border border-teal/40 text-teal px-7 py-3.5 rounded-full hover:border-teal hover:bg-teal hover:text-ivory transition-colors"
            >
              Read our story
            </Link>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

const HomeFaq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 lg:py-28 bg-white border-t border-cloud">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Common Questions</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
            The questions every NSW property owner asks
          </h2>
        </FadeIn>
        <div className="mt-10 border-t border-cloud">
          {FAQS.slice(0, 4).map((f, i) => (
            <FadeIn key={f.q} delay={i * 0.05}>
              <div className="border-b border-cloud" data-testid={`faq-accordion-item-${i + 1}`}>
                <button
                  className="w-full flex items-center justify-between gap-4 py-6 text-left"
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  <span className="font-serif text-lg text-teal">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-teal shrink-0 transition-transform duration-300 ${
                      open === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {open === i && (
                  <p className="pb-6 text-charcoal/75 leading-relaxed max-w-3xl">{f.a}</p>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="mt-8">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-teal font-medium border-b-2 border-sand pb-1 hover:border-teal transition-colors"
          >
            Browse all FAQs <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section className="py-20 lg:py-28 bg-sand-light/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Eyebrow>Early Readers</Eyebrow>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-teal">
          What landowners are saying
        </h2>
      </FadeIn>
      <div className="mt-12 grid sm:grid-cols-3 gap-6" data-testid="testimonials-grid">
        {TESTIMONIALS.map((t, i) => (
          <FadeIn key={i} delay={i * 0.07} className="h-full">
            <figure className="h-full bg-white border border-cloud p-7 flex flex-col" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 text-sand" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-4 text-charcoal/80 leading-relaxed flex-1">"{t.quote}"</blockquote>
              <figcaption className="mt-5 text-xs font-mono uppercase tracking-[0.14em] text-charcoal/55">
                {t.name} · {t.context}
              </figcaption>
            </figure>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="home-page">
    <Seo />
    <BrandBanner />
    <Hero />
    <Marquee />
    <DaFlagship />
    <FoundingCollection />
    <Pathways />
    <Methodology />
    <Audiences />
    <FeaturedGuide />
    <FeaturedToolkits />
    <Testimonials />
    <AdvisorSpotlight />
    <HomeFaq />
  </div>
);

export default Home;
